import React, { useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Stack,
  Badge,
  Group,
  Box,
  Paper,
  ActionIcon,
  Textarea,
  MultiSelect,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { RecipeInstruction } from '../../types/mealPlan';

interface SortableInstructionEditorProps {
  id: string;
  index: number;
  instruction: RecipeInstruction;
  availableIngredients: string[];
  instructionRef: React.RefObject<HTMLTextAreaElement[]>;
  onTextChange: (index: number, text: string) => void;
  onIngredientsChange: (index: number, ingredients: string[]) => void;
  onRemove: (index: number) => void;
}

export const SortableInstructionEditor: React.FC<SortableInstructionEditorProps> = ({
  id,
  index,
  instruction,
  availableIngredients,
  instructionRef,
  onTextChange,
  onIngredientsChange,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const [text, setText] = useState(instruction.text);
  const [ingredients, setIngredients] = useState(instruction.ingredients || []);

  useEffect(() => {
    onTextChange(index, text);
  }, [text, index, onTextChange]);

  useEffect(() => {
    onIngredientsChange(index, ingredients);
  }, [ingredients, index, onIngredientsChange]);

  useEffect(() => {
    setText(instruction.text);
    setIngredients(instruction.ingredients || []);
  }, [instruction.text, instruction.ingredients]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    boxShadow: isDragging ? '0 5px 15px rgba(0, 0, 0, 0.3)' : undefined,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={{
        ...style,
        position: 'relative',
      }}
      p="md"
      radius="lg"
      withBorder
    >
      {/* Drag handle overlay covering entire card */}
      <Box
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'manipulation',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          zIndex: 0,
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
      <ActionIcon
        color="orange"
        variant="subtle"
        onClick={() => onRemove(index)}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          pointerEvents: 'auto',
        }}
      >
        <IconX size={16} />
      </ActionIcon>
      <Stack gap="md" style={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
        <Group gap="md" align="flex-start" wrap="nowrap">
          <Badge
            size="xl"
            variant="filled"
            color="blue"
            circle
            style={{
              transition: 'transform 0.2s ease',
              transform: isDragging ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            {index + 1}
          </Badge>
          <Box style={{ flex: 1, paddingRight: 32, minWidth: 0 }}>
            <Textarea
              value={text}
              variant="unstyled"
              onChange={(event) => setText(event.currentTarget.value)}
              placeholder="Enter instruction step..."
              rows={3}
              maxLength={1000}
              ref={(el) => {
                if (instructionRef.current && el) {
                  instructionRef.current[index] = el;
                }
              }}
              styles={{
                input: {
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  cursor: 'text',
                  pointerEvents: 'auto',
                }
              }}
            />
            {availableIngredients.length > 0 && (
              <Box mt="sm" style={{ pointerEvents: 'auto' }}>
                <MultiSelect
                  label="Ingredients used in this step"
                  variant="unstyled"
                  placeholder={ingredients.length ? undefined : "Select ingredients relevant to this step..."}
                  value={ingredients}
                  onChange={setIngredients}
                  data={Array.from(new Set(availableIngredients)).map(name => ({ value: name, label: name }))}
                  searchable
                  size="sm"
                  styles={{
                    section: {
                      alignItems: 'flex-start',
                      paddingTop: '7px'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Group>
      </Stack>
    </Paper>
  );
};

export default SortableInstructionEditor;
